import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ShieldCheck, DownloadCloud, Radio, Plus, Minus } from "lucide-react";
import api from "@/lib/api";
import { IMG } from "@/lib/imagery";
import VideoEmbed from "@/components/VideoEmbed";
import Seo from "@/components/Seo";
import PromoStrip, { usePromo } from "@/components/PromoStrip";

const FAQS = [
  { q: "What does “account-bound” mean?", a: "Every EA and indicator validates against your MT4/MT5 login number on load. A leaked file is useless without your account, and you can rebind up to three times from your dashboard." },
  { q: "How do I receive my files?", a: "Instantly. Once payment clears, your license and downloadable files appear on your dashboard and product page — no manual approval needed." },
  { q: "Which payment methods do you accept?", a: "Cards and mobile money via PesaPal, and crypto (BTC, ETH, USDT) via NowPayments." },
  { q: "How do signals work?", a: "A monthly subscription unlocks the on-site feed and an auto-generated Telegram invite. Access is revoked automatically when the subscription lapses." },
  { q: "Can I get a refund?", a: "Digital tools are licensed per account. Reach out via the support chat and we’ll make it right wherever we can." },
];

const DEFAULT_FEATURES = [
  { title: "Bound to your account.", body: "Every EA and indicator is validated against your MT4/MT5 login on load. Non-transferable, revocable, and yours alone." },
  { title: "One desk, everything owned.", body: "Licenses, signals, courses — gated to what you paid for, revoked when subscriptions lapse. No cross-access." },
  { title: "Built by traders on the desk.", body: "We publish only what we deploy ourselves. Nothing that has not survived real orders makes the catalogue." },
];

const DEFAULT_STATS = [
  { value: "6+ yrs", label: "On the live trading desk" },
  { value: "MT4 · MT5", label: "Account-bound licensing" },
  { value: "3×", label: "Rebinds included per license" },
  { value: "24/7", label: "Signals feed & dashboard" },
];

const DEFAULT_STEPS = [
  { title: "Buy & bind", body: "Purchase an EA, indicator or course. Your license is issued instantly and bound to your MT4/MT5 account number." },
  { title: "Download & install", body: "Grab your files straight from your dashboard and drop them into MetaTrader. Every release is version-tracked." },
  { title: "Trade & track", body: "Signals mirror to your on-site feed and Telegram. Manage licenses, rebinds and subscriptions from one desk." },
];

const STEP_ICONS = [ShieldCheck, DownloadCloud, Radio];

function Faq({ faqs }) {
  const [open, setOpen] = useState(0);
  return (
    <div className="divide-y divide-[color:var(--ink-hairline)] border-y border-[color:var(--ink-hairline)]">
      {faqs.map((f, i) => (
        <div key={i} data-testid={`faq-${i}`}>
          <button onClick={() => setOpen(open === i ? -1 : i)} className="flex w-full items-center justify-between gap-4 py-5 text-left">
            <span className="font-display text-lg font-medium text-[color:var(--ink)]">{f.q}</span>
            {open === i ? <Minus size={18} className="shrink-0 text-[color:var(--evergreen)]" /> : <Plus size={18} className="shrink-0 text-[color:var(--ink-muted)]" />}
          </button>
          {open === i && <p className="pb-6 pr-8 text-[15px] leading-relaxed text-[color:var(--ink-body)]">{f.a}</p>}
        </div>
      ))}
    </div>
  );
}

export default function Landing() {
  const [categories, setCategories] = useState([]);
  const [page, setPage] = useState(null);
  const promo = usePromo();
  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/pages/landing").then((r) => setPage(r.data)).catch(() => {});
  }, []);

  return (
    <div className="relative">
      <Seo description="HK Forexedge builds account-bound MT4/MT5 indicators, expert advisors, curated signals and trading courses — delivered instantly to one gated operator's dashboard." />
      <PromoStrip promo={promo} />
      {/* HERO — editorial magazine */}
      <section className="relative">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 pb-16 pt-16 lg:grid-cols-[1.25fr_1fr] lg:gap-24 lg:px-8 lg:pb-24 lg:pt-24">
          <div className="relative">
            <span className="pill-urgent" data-testid="hero-live-pill">
              <span className="live-dot" /> Signals desk live · broadcasting now
            </span>
            <p className="mt-6 overline">{page?.hero_kicker || "Account-bound trading tools"}</p>
            <h1
              data-testid="hero-title"
              className="mt-6 font-editorial text-[52px] font-semibold leading-[1.02] tracking-[-0.02em] text-[color:var(--ink)] sm:text-[72px] lg:text-[92px]"
            >
              {page?.hero_title || (<>Instruments,<br />expert advisors,<br />and signals.</>)}
            </h1>
            <p className="mt-8 max-w-xl text-[17px] leading-relaxed text-[color:var(--ink-body)]">
              {page?.hero_subtitle ||
                "HK Forexedge builds account-bound trading tools that survive contact with real markets. Every purchase lands in one unified operator's desk, tied to your MT4 or MT5 account number."}
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link to="/store/expert-advisors" className="btn-edge" data-testid="hero-cta-primary">
                Browse the catalogue <ArrowUpRight size={16} />
              </Link>
              <Link to="/register" className="btn-edge-ghost" data-testid="hero-cta-secondary">
                Create an account
              </Link>
            </div>
          </div>

          {/* Feature photo */}
          <div className="relative">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="photo-vignette h-[420px] lg:h-[520px]"
            >
              <img src={IMG.matrix} alt="" className="h-full w-full object-cover" />
            </motion.div>
            <div className="mt-4 flex items-baseline justify-between text-[color:var(--ink-muted)]">
              <p className="font-editorial italic">
                An instrument, in the classical sense.
              </p>
              <span className="text-xs">Fig. 01</span>
            </div>
          </div>
        </div>

        <hr className="rule mx-auto max-w-7xl" />
      </section>

      {/* OPTIONAL VIDEO + admin sections */}
      {page?.hero_video_url && (
        <section className="mx-auto max-w-5xl px-5 py-14 lg:px-8" data-testid="landing-video">
          <p className="overline mb-4">Watch</p>
          <VideoEmbed url={page.hero_video_url} />
        </section>
      )}
      {page?.sections?.length > 0 && (
        <section className="mx-auto max-w-7xl px-5 py-12 lg:px-8">
          <div className="grid gap-14">
            {page.sections.map((s, i) => (
              <div key={i} className="grid items-center gap-8 lg:grid-cols-2">
                <div>
                  {s.heading && <h3 className="font-editorial text-3xl font-semibold tracking-tight text-[color:var(--ink)]">{s.heading}</h3>}
                  {s.body && <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-[color:var(--ink-body)]">{s.body}</p>}
                </div>
                {s.video_url ? (
                  <VideoEmbed url={s.video_url} />
                ) : s.image_url ? (
                  <div className="photo-vignette h-[300px]"><img src={s.image_url} alt="" className="h-full w-full object-cover" /></div>
                ) : null}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CREDENTIALS — three plain columns */}
      <section className="mx-auto max-w-7xl px-5 py-16 lg:px-8 lg:py-20">
        <div className="grid gap-10 lg:grid-cols-3 lg:gap-16">
          {(page?.features?.length ? page.features : DEFAULT_FEATURES).map((c, i) => (
            <div key={i}>
              <p className="numeral text-4xl">{String(i + 1).padStart(2, "0")}</p>
              <h3 className="mt-3 font-editorial text-2xl leading-tight text-[color:var(--ink)]">
                {c.title}
              </h3>
              <p className="mt-4 text-[15px] leading-relaxed text-[color:var(--ink-body)]">
                {c.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      <hr className="rule mx-auto max-w-7xl" />

      {/* CATALOGUE */}
      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
        <div className="mb-14 flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <h2 className="max-w-2xl font-editorial text-4xl font-normal leading-[1.05] tracking-tight text-[color:var(--ink)] sm:text-5xl lg:text-6xl">
            The catalogue.
          </h2>
          <p className="max-w-md text-[15px] leading-relaxed text-[color:var(--ink-body)]">
            Four surfaces. One dashboard. Everything you buy lands where you
            work.
          </p>
        </div>

        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4 lg:gap-10">
          {categories.map((c, i) => {
            const live = !c.coming_soon;
            const to = c.slug === "signals" ? "/signals" : `/store/${c.slug}`;
            return (
              <motion.div
                key={c.slug}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
              >
                <Link
                  to={to}
                  data-testid={`category-${c.slug}`}
                  className="group block"
                >
                  <div className="photo-vignette aspect-[4/5] w-full">
                    {c.hero_image && (
                      <img
                        src={c.hero_image}
                        alt=""
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                      />
                    )}
                  </div>
                  <div className="mt-4 flex items-baseline justify-between">
                    <h3 className="font-editorial text-2xl text-[color:var(--ink)]">
                      {c.name}
                    </h3>
                    <span className="text-xs text-[color:var(--ink-muted)]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <p className="mt-2 text-[14px] leading-relaxed text-[color:var(--ink-body)]">
                    {c.description}
                  </p>
                  <p className="mt-3 flex items-center gap-1.5 text-sm text-[color:var(--evergreen)]">
                    {live ? "Enter" : "Announcing soon"}
                    <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </p>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </section>

      <hr className="rule mx-auto max-w-7xl" />

      {/* PHILOSOPHY — big editorial quote */}
      <section className="mx-auto max-w-5xl px-5 py-24 lg:py-32">
        <p className="font-editorial italic text-[color:var(--ink-muted)]">
          On method
        </p>
        <blockquote className="mt-6 font-editorial text-3xl font-normal leading-[1.2] tracking-tight text-[color:var(--ink)] sm:text-4xl lg:text-[52px]">
          <span className="text-[color:var(--evergreen)]">"</span>
          {page?.quote || "The market does not reward the loudest tools. It rewards the ones you are willing to trust on the seventh consecutive Monday. That is what we build."}
          <span className="text-[color:var(--evergreen)]">"</span>
        </blockquote>
        <p className="mt-8 font-editorial italic text-[color:var(--ink-muted)]">
          {page?.quote_attribution || "— HK Forexedge desk, since 2019"}
        </p>
      </section>

      <hr className="rule mx-auto max-w-7xl" />

      {/* BY THE NUMBERS */}
      <section className="mx-auto max-w-7xl px-5 py-16 lg:px-8 lg:py-20">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {(page?.stats?.length ? page.stats : DEFAULT_STATS).map((s, i) => (
            <div key={i} className="border-l-2 border-[color:var(--evergreen)] pl-5">
              <p className="font-editorial text-4xl text-[color:var(--ink)]">{s.value}</p>
              <p className="mt-2 text-sm text-[color:var(--ink-body)]">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      <hr className="rule mx-auto max-w-7xl" />

      {/* HOW IT WORKS */}
      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-24">
        <h2 className="max-w-2xl font-editorial text-4xl font-normal leading-[1.05] tracking-tight text-[color:var(--ink)] sm:text-5xl">
          From checkout to chart in minutes.
        </h2>
        <div className="mt-14 grid gap-10 lg:grid-cols-3 lg:gap-16">
          {(page?.steps?.length ? page.steps : DEFAULT_STEPS).map((c, i) => {
            const Icon = STEP_ICONS[i % STEP_ICONS.length];
            return (
              <div key={i}>
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[color:var(--evergreen-soft)] text-[color:var(--evergreen)]"><Icon size={20} /></span>
                <p className="numeral mt-5 text-sm">{String(i + 1).padStart(2, "0")}</p>
                <h3 className="mt-2 font-editorial text-2xl text-[color:var(--ink)]">{c.title}</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-[color:var(--ink-body)]">{c.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      <hr className="rule mx-auto max-w-7xl" />

      {/* FAQ */}
      <section className="mx-auto max-w-4xl px-5 py-20 lg:py-24">
        <p className="overline mb-3">Questions</p>
        <h2 className="font-editorial text-4xl font-normal tracking-tight text-[color:var(--ink)] sm:text-5xl">Before you buy.</h2>
        <div className="mt-10"><Faq faqs={page?.faqs?.length ? page.faqs : FAQS} /></div>
      </section>

      <hr className="rule mx-auto max-w-7xl" />

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
        <div className="grid items-center gap-10 lg:grid-cols-[1.5fr_1fr]">
          <div>
            <h3 className="font-editorial text-4xl font-normal leading-[1.05] tracking-tight text-[color:var(--ink)] sm:text-5xl">
              {page?.cta_title || "Bind an account. Take the desk."}
            </h3>
            <p className="mt-5 max-w-xl text-[16px] leading-relaxed text-[color:var(--ink-body)]">
              {page?.cta_body || "Every purchase, license, signal subscription, and course lesson — gated to what you paid for, revoked when subscriptions lapse."}
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row lg:justify-end">
            <Link to="/register" className="btn-edge" data-testid="footer-cta-register">
              Get access <ArrowUpRight size={16} />
            </Link>
            <Link to="/login" className="btn-edge-ghost" data-testid="footer-cta-login">
              I already have one
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
